import React from "react";

/**
 * ErrorBoundary — Catches React render errors and prevents blank-page crashes.
 * Used at the root of the app so any downstream component crash is contained
 * and the user sees a friendly fallback with a reload button, not a white page.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Emit to console + posthog if available
    console.error("[ErrorBoundary]", error, info);
    try {
      if (window.posthog?.captureException) {
        window.posthog.captureException(error, { extra: info });
      }
    } catch { /* ignore */ }
  }

  handleReload = () => {
    try { localStorage.removeItem("progere_draft_quote_v1"); } catch { /* ignore */ }
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isFr = (typeof navigator !== "undefined" && /^fr/i.test(navigator.language));
    const t = isFr ? {
      title: "Une erreur s'est produite",
      subtitle: "L'application a rencontré un problème. Rechargez la page pour continuer — vos données sont sauvegardées.",
      reload: "Recharger",
      home: "Retour à l'accueil",
      details: "Détails techniques",
    } : {
      title: "Something went wrong",
      subtitle: "The app hit an unexpected error. Reload to continue — your data is safe.",
      reload: "Reload",
      home: "Back to home",
      details: "Technical details",
    };

    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        background: "#0B0C0F",
        color: "#F5F5F5",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', sans-serif",
      }} data-testid="error-boundary">
        <div style={{
          maxWidth: 480,
          textAlign: "center",
          padding: "32px",
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(212,175,55,0.35)",
          borderRadius: "24px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
        }}>
          <div style={{
            width: 64,
            height: 64,
            borderRadius: "16px",
            background: "linear-gradient(180deg, #F5D062 0%, #D4AF37 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px",
            fontWeight: 900,
            fontSize: 22,
            color: "#0B0C0F",
          }}>
            !
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: "0 0 12px", color: "#F5F5F5" }}>{t.title}</h1>
          <p style={{ fontSize: 14, lineHeight: 1.55, margin: "0 0 24px", color: "rgba(245,245,245,0.7)" }}>{t.subtitle}</p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={this.handleReload} data-testid="error-reload"
              style={{
                background: "linear-gradient(180deg, #F5D062 0%, #D4AF37 100%)",
                color: "#0B0C0F",
                border: "none",
                padding: "10px 22px",
                borderRadius: "999px",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
              }}>
              {t.reload}
            </button>
            <a href="/" data-testid="error-home"
              style={{
                background: "rgba(255,255,255,0.05)",
                color: "rgba(245,245,245,0.85)",
                border: "1px solid rgba(255,255,255,0.1)",
                padding: "10px 22px",
                borderRadius: "999px",
                fontSize: 13,
                fontWeight: 700,
                textDecoration: "none",
              }}>
              {t.home}
            </a>
          </div>
          {this.state.error && (
            <details style={{ marginTop: 24, textAlign: "left", fontSize: 11, color: "rgba(245,245,245,0.45)" }}>
              <summary style={{ cursor: "pointer" }}>{t.details}</summary>
              <pre style={{ overflow: "auto", maxHeight: 160, margin: "8px 0 0", padding: 8, background: "rgba(0,0,0,0.4)", borderRadius: 8 }}>
                {String(this.state.error?.message || this.state.error)}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
